import React from "react";
import { FormProvider } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { transformDuration } from "@sscs/utils";
import { NOTIFY_ON_COMPLETION_LABEL } from "Constants";
import { useSavePolicyForm } from "../../hooks";
import styles from "./savePolicy.module.scss";

type SavePolicyProps = {
  onClose: () => void;
  handleSave: (data: unknown) => void;
  isOpen: boolean;
  reprocessEstimate: number;
};

const SavePolicyContent = ({
  onClose,
  handleSave,
  reprocessEstimate,
}: Omit<SavePolicyProps, "isOpen">): React.JSX.Element => {
  const { onSubmit, errors, methods } = useSavePolicyForm({
    handleSave,
    onClose,
  });
  const reprocessAffected = methods.watch("reprocess_affected");

  return (
    <FormProvider {...methods}>
      <Form.Form
        onSubmit={onSubmit}
        aria-label="Save policy configuration form"
      >
        <Dialog.Header>
          <Dialog.Header.Title>Save policy configuration</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            {/* Body prose, not `Section.Description`: these two paragraphs are
                  the instructions the choice below depends on, so they carry full
                  body weight rather than the Description's fainter gloss. */}
            <p>
              To immediately save your policy configuration changes, select
              {' "Save"'}. If saved without reanalyzing, all uploaded files will
              be marked as outdated and can be manually reanalyzed.
            </p>
            <p>
              To save your changes and reanalyze all uploaded files, select the
              checkbox below, then select {'"Save"'}. Any newly uploaded files
              will be added to the back of the queue and wait to be analyzed for
              approximately{" "}
              <strong>{transformDuration(reprocessEstimate)}</strong>.
            </p>
            <Dialog.Section.Content>
              <Form.Checkbox
                name="reprocess_affected"
                label={
                  <div>
                    <div>Reanalyze when saved</div>
                    <strong>
                      Estimated time: {transformDuration(reprocessEstimate)}
                    </strong>
                  </div>
                }
                errors={errors}
              />
              <Form.Checkbox
                name="notify_on_completion"
                errors={errors}
                label={NOTIFY_ON_COMPLETION_LABEL}
                className={
                  !reprocessAffected ? styles["hide-dependent-field"] : ""
                }
                isDisabled={!reprocessAffected}
              />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="primary">
              Save
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const SavePolicy = ({
  isOpen,
  ...props
}: SavePolicyProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <SavePolicyContent {...props} />
  </Dialog.Frame>
);

export default SavePolicy;
